import { createFileRoute, Link } from "@tanstack/react-router";
import { Building2, Lock, Shield, BarChart3, Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import { useEffect } from "react";
import { trackEvent } from "@/lib/audit";
import { BrandSlogan } from "@/components/brand/BrandSlogan";
import { requireEnterpriseAccess } from "@/lib/security/enterpriseRouteGate";

export const Route = createFileRoute("/partners")({
  beforeLoad: () => requireEnterpriseAccess("/partners"),
  head: () => ({
    meta: [
      { title: "Partner Portal · VerseSport — Inteligencia Deportiva en Tiempo Real" },
      { name: "description", content: "Acceso corporativo a VerseSport — Inteligencia Deportiva en Tiempo Real. Para broadcasters, marcas y sponsors." },
      { property: "og:title", content: "Partner Portal · VerseSport — Inteligencia Deportiva en Tiempo Real" },
      { property: "og:description", content: "Acceso corporativo a VerseSport — Inteligencia Deportiva en Tiempo Real." },
    ],
  }),
  component: PartnersLanding,
});

function PartnersLanding() {
  useEffect(() => {
    void trackEvent("partner_portal_link_visible", { surface: "partners_landing" });
  }, []);
  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      <header className="mb-10 flex flex-col items-start gap-3">
        <div className="flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-[11px] font-black uppercase tracking-widest text-primary">
          <Building2 className="h-3.5 w-3.5" /> Corporate Access
        </div>
        <h1 className="text-3xl font-black tracking-tight sm:text-5xl">Partner Portal</h1>
        <BrandSlogan section="partner_portal" size="md" align="left" withProduct className="mt-1" />
        <p className="max-w-2xl text-sm text-muted-foreground sm:text-base">
          Acceso corporativo controlado a la inteligencia de la plataforma VerseSport para
          broadcasters, marcas patrocinadoras y operadores comerciales.
        </p>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/partners/login"
            onClick={() => void trackEvent("partner_login_success", { surface: "landing_cta", stage: "cta" })}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-black text-primary-foreground shadow hover:opacity-90"
          >
            <Lock className="h-4 w-4" /> Acceder al Portal Corporativo
          </Link>
          <Link
            to="/corporate-access"
            onClick={() => void trackEvent("corporate_access_link_clicked", { surface: "partners_landing" })}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-4 py-2 text-sm font-bold hover:bg-accent"
          >
            Request Partner Access <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </header>

      <section className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card icon={<BarChart3 className="h-5 w-5 text-primary" />} title="Benefits Overview"
          items={["Executive dashboard en vivo", "Campaign performance & ROI", "Audience analytics agregadas", "Geographic insights"]} />
        <Card icon={<Shield className="h-5 w-5 text-[oklch(0.72_0.18_220)]" />} title="Security & Compliance"
          items={["RLS multi-tenant aislamiento total", "Datos agregados, sin PII", "Auditoría de cada acceso", "Read-only por defecto"]} />
        <article className="rounded-2xl border border-border bg-background/60 p-5">
          <div className="mb-2 flex items-center gap-2 text-sm font-black">
            <CheckCircle2 className="h-5 w-5 text-[oklch(0.82_0.24_145)]" /> Demo Sandbox
          </div>
          <p className="text-xs text-muted-foreground">
            Acceso restringido. Las credenciales sandbox se entregan únicamente
            a partners aprobados por el equipo enterprise tras la solicitud.
          </p>
          <Link
            to="/corporate-access"
            className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/5 px-3 py-1.5 text-xs font-black text-primary hover:bg-primary/10"
          >
            Solicitar acceso enterprise <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </article>
      </section>

      <section className="mt-10 rounded-2xl border border-border bg-background/60 p-6">
        <div className="flex items-center gap-2 text-sm font-black">
          <Mail className="h-4 w-4 text-primary" /> Contact Sales
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Para acuerdos enterprise, licenciamiento global o integración white-label,
          escribe a <a className="text-primary underline" href="mailto:sales@versesport.com">sales@versesport.com</a>.
        </p>
      </section>
    </div>
  );
}

function Card({ icon, title, items }: { icon: React.ReactNode; title: string; items: string[] }) {
  return (
    <article className="rounded-2xl border border-border bg-background/60 p-5">
      <div className="mb-2 flex items-center gap-2 text-sm font-black">{icon} {title}</div>
      <ul className="space-y-1 text-xs text-muted-foreground">
        {items.map((i) => <li key={i} className="flex items-start gap-1.5"><span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary" /> {i}</li>)}
      </ul>
    </article>
  );
}